import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardPaste, Ship, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { BarcodeField } from '@/components/work/BarcodeField';
import { describeWorkError } from '@/features/workMode/api';
import { useCustomerOptions, useSalesOrderOptions, useShipUnits } from '@/features/traceability/hooks';
import { shipToPlace, UNIT_STATUS_LABEL, type ShipUnitsResult } from '@/features/traceability/types';
import { formatDate } from '@/lib/formatters';

interface ShipUnitsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSerials?: string[];
}

type Mode = 'order' | 'customer' | 'other';

function localDateTimeValue(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * "A container of 210 units to Germany." Pick where they are going (a sales
 * order, a customer, or a free-text destination), then scan every barcode.
 * Each unit is stamped with the sale; the result lists anything that did not
 * take so the pallet can be checked before the doors close.
 */
export function ShipUnitsDialog({ open, onOpenChange, initialSerials = [] }: ShipUnitsDialogProps) {
  const [mode, setMode] = useState<Mode>('order');
  const [orderId, setOrderId] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [destination, setDestination] = useState('');
  const [shippedAt, setShippedAt] = useState(localDateTimeValue());
  const [note, setNote] = useState('');
  const [serials, setSerials] = useState<string[]>([]);
  const [current, setCurrent] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [result, setResult] = useState<ShipUnitsResult | null>(null);

  const { data: orders = [] } = useSalesOrderOptions(open);
  const { data: customers = [] } = useCustomerOptions(open);
  const ship = useShipUnits();

  useEffect(() => {
    if (!open) return;
    setMode('order');
    setOrderId('');
    setCustomerId('');
    setDestination('');
    setShippedAt(localDateTimeValue());
    setNote('');
    setSerials([...new Set(initialSerials.map((s) => s.trim()).filter(Boolean))]);
    setCurrent('');
    setPasteOpen(false);
    setPasteText('');
    setResult(null);
    // initialSerials is a fresh array each render; keyed on open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const order = useMemo(() => orders.find((o) => o.id === orderId) ?? null, [orders, orderId]);

  const addSerial = (raw: string) => {
    const s = raw.trim();
    if (!s) return;
    setSerials((list) => {
      if (list.some((x) => x.toUpperCase() === s.toUpperCase())) {
        toast.message(`${s} is already in the list.`);
        return list;
      }
      return [...list, s];
    });
    setCurrent('');
  };

  const addPasted = () => {
    const parts = pasteText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setSerials((list) => {
      const seen = new Set(list.map((x) => x.toUpperCase()));
      const next = [...list];
      for (const p of parts) {
        if (!seen.has(p.toUpperCase())) {
          seen.add(p.toUpperCase());
          next.push(p);
        }
      }
      return next;
    });
    setPasteText('');
    setPasteOpen(false);
  };

  const destinationOk = mode === 'order' ? !!orderId : mode === 'customer' ? !!customerId : destination.trim().length > 0;

  const submit = async () => {
    if (!destinationOk) {
      toast.error('Pick where the units are going first.');
      return;
    }
    if (serials.length === 0) {
      toast.error('Scan at least one serial number.');
      return;
    }
    try {
      const res = await ship.mutateAsync({
        serials,
        salesOrderId: mode === 'order' ? orderId : null,
        customerId: mode === 'customer' ? customerId : mode === 'order' ? (order?.customer_id ?? null) : null,
        destination:
          mode === 'other'
            ? destination.trim()
            : destination.trim() || (mode === 'order' ? shipToPlace(order?.ship_to) : null),
        shippedAt: shippedAt ? new Date(shippedAt).toISOString() : null,
        note: note.trim() || null,
      });
      setResult(res);
      const problems = res.not_found.length + res.already_shipped.length + res.blocked.length;
      if (problems === 0) toast.success(`${res.shipped.length} unit${res.shipped.length === 1 ? '' : 's'} marked shipped.`);
      else toast.message(`${res.shipped.length} shipped · ${problems} need${problems === 1 ? 's' : ''} a look.`);
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  };

  const whereLabel =
    mode === 'order' && order
      ? `${order.customers?.name ?? order.ship_to?.name ?? order.customer_email ?? 'Order'} · ${shipToPlace(order.ship_to) ?? 'SO ' + order.so_number}`
      : mode === 'customer'
        ? (customers.find((c) => c.id === customerId)?.name ?? '')
        : destination.trim();

  return (
    <Dialog open={open} onOpenChange={(o) => !ship.isPending && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Ship className="h-5 w-5 text-atmosphere-dark" />
            Ship units
          </DialogTitle>
          <DialogDescription>Say where they are going, then scan every barcode on the pallet.</DialogDescription>
        </DialogHeader>

        {result ? (
          <ResultView result={result} whereLabel={whereLabel} onDone={() => onOpenChange(false)} onMore={() => setResult(null)} />
        ) : (
          <>
            <div className="space-y-6 px-6 py-5">
              <section className="space-y-3">
                <Label className="text-base">Where are they going?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      ['order', 'Sales order'],
                      ['customer', 'Customer'],
                      ['other', 'Somewhere else'],
                    ] as Array<[Mode, string]>
                  ).map(([m, label]) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      className={cn(
                        'min-h-[44px] rounded-xl border px-3 text-sm font-medium transition-colors',
                        mode === m ? 'border-info bg-info-soft/30 text-info' : 'border-border text-foreground hover:bg-secondary',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {mode === 'order' && (
                  <Select value={orderId} onValueChange={setOrderId}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={orders.length ? 'Pick the sales order' : 'No sales orders found'} />
                    </SelectTrigger>
                    <SelectContent>
                      {orders.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          <span className="font-mono text-xs">{o.so_number}</span> · {o.customers?.name ?? o.ship_to?.name ?? o.customer_email ?? 'Customer'}
                          {shipToPlace(o.ship_to) ? ` · ${shipToPlace(o.ship_to)}` : ''} · {formatDate(o.ordered_at)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {mode === 'customer' && (
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={customers.length ? 'Pick the customer' : 'No customers found'} />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                          {c.shipping_address?.country ? ` · ${c.shipping_address.country}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ship-dest">{mode === 'other' ? 'Destination' : 'Destination (optional)'}</Label>
                    <Input
                      id="ship-dest"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      placeholder={mode === 'other' ? 'e.g. Germany — Hamburg, container MSKU1234567' : 'e.g. container MSKU1234567'}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ship-when">Shipped on</Label>
                    <Input id="ship-when" type="datetime-local" value={shippedAt} onChange={(e) => setShippedAt(e.target.value)} className="h-11" />
                  </div>
                </div>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for every unit's timeline (optional)" className="h-11" />
              </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Scan the units</Label>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    {serials.length} unit{serials.length === 1 ? '' : 's'}
                  </span>
                </div>
                <BarcodeField value={current} onChange={setCurrent} onScanned={addSerial} placeholder="Scan a serial — each scan adds a unit" autoFocus={open} />
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPasteOpen((o) => !o)}>
                    <ClipboardPaste className="h-4 w-4" />
                    Paste a list
                  </Button>
                  {serials.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setSerials([])}>
                      Clear all
                    </Button>
                  )}
                </div>
                {pasteOpen && (
                  <div className="space-y-2 rounded-xl border border-border p-3">
                    <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={4} placeholder="One serial per line, or separated by commas or spaces." className="font-mono text-sm" />
                    <div className="flex justify-end">
                      <Button type="button" size="sm" onClick={addPasted} disabled={!pasteText.trim()}>
                        Add to list
                      </Button>
                    </div>
                  </div>
                )}
                {serials.length > 0 && (
                  <ul className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto rounded-xl border border-border p-2">
                    {serials.map((s) => (
                      <li key={s} className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-1 pl-2.5 pr-1 font-mono text-xs">
                        {s}
                        <button type="button" onClick={() => setSerials((l) => l.filter((x) => x !== s))} aria-label={`Remove ${s}`} className="rounded-full p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground">
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
              <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)} disabled={ship.isPending}>
                Cancel
              </Button>
              <Button size="lg" onClick={submit} disabled={ship.isPending || serials.length === 0 || !destinationOk}>
                {ship.isPending ? 'Saving…' : `Mark ${serials.length || ''} unit${serials.length === 1 ? '' : 's'} shipped`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultView({ result, whereLabel, onDone, onMore }: { result: ShipUnitsResult; whereLabel: string; onDone: () => void; onMore: () => void }) {
  const problems = result.not_found.length + result.already_shipped.length + result.blocked.length;
  return (
    <>
      <div className="space-y-5 px-6 py-5">
        <div className="flex items-center gap-3 rounded-xl border border-bright-moss bg-bright-moss/15 px-4 py-3">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-bright-moss-dark" />
          <div>
            <p className="text-base font-medium text-foreground">
              {result.shipped.length} unit{result.shipped.length === 1 ? '' : 's'} marked shipped
            </p>
            {whereLabel && <p className="text-sm text-muted-foreground">{whereLabel}</p>}
          </div>
        </div>
        {problems > 0 && (
          <div className="space-y-3 rounded-xl border border-fresh-salmon/70 bg-fresh-salmon/10 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-fresh-salmon-dark">
              <AlertTriangle className="h-4 w-4" />
              {problems} serial{problems === 1 ? '' : 's'} did not take
            </p>
            <ProblemList title="Not in the log" items={result.not_found} />
            <ProblemList title="Already shipped" items={result.already_shipped} />
            <ProblemList title="Blocked" items={result.blocked.map((b) => `${b.serial} · ${UNIT_STATUS_LABEL[b.status]}`)} />
          </div>
        )}
        {result.shipped.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">Shipped serials</summary>
            <p className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-foreground">{result.shipped.join('\n')}</p>
          </details>
        )}
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
        <Button variant="ghost" size="lg" onClick={onMore}>
          Ship more
        </Button>
        <Button size="lg" onClick={onDone}>
          Done
        </Button>
      </div>
    </>
  );
}

function ProblemList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-foreground">{items.join('\n')}</p>
    </div>
  );
}
